/**
 * Build-a-Box Cart Protection (ES Module)
 *
 * Prevents customers from changing individual quantities or removing
 * individual build-a-box items in the cart. The only option is to
 * remove the entire bundle at once (with a confirmation warning).
 *
 * Strategy: Monkey-patch CartItemsComponent prototype methods
 * (onLineItemRemove, updateQuantity) so interception survives DOM
 * morphing and fires before any cart API calls.
 */

const BAB_PROP_KEY = '_build_a_box';
const BAB_PROP_VALUE = 'true';

let babLines = {};
let babKeys = new Set();
let isRemoving = false;

// ── Styles ──────────────────────────────────────────────────────────

const babDynamicStyle = document.createElement('style');
babDynamicStyle.id = 'bab-cart-protection-dynamic';
document.head.appendChild(babDynamicStyle);

if (!document.getElementById('bab-cart-protection-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'bab-cart-protection-styles';
  styleEl.textContent = [
    '.bab-bundle-badge {',
    '  display: inline-flex; align-items: center; gap: 6px;',
    '  font-size: 0.75rem; font-weight: 600; color: #113e30;',
    '  background: #f5f2e2; padding: 4px 10px; border-radius: 20px;',
    '  white-space: nowrap; line-height: 1.4;',
    '}',
    '.bab-bundle-badge svg { width: 14px; height: 14px; flex-shrink: 0; }',
    '.bab-remove-confirm {',
    '  background: #f5f2e2;',
    '  padding: 16px 20px; margin-bottom: 16px;',
    '  animation: babConfirmIn 200ms ease-out;',
    '}',
    '@keyframes babConfirmIn {',
    '  from { opacity: 0; transform: translateY(-8px); }',
    '  to { opacity: 1; transform: translateY(0); }',
    '}',
    '.bab-remove-confirm__text {',
    '  font-size: 0.875rem; font-weight: 600; color: #113e30;',
    '  margin: 0 0 12px; line-height: 1.4;',
    '}',
    '.bab-remove-confirm__actions { display: flex; gap: 8px; flex-wrap: wrap; }',
    '.bab-remove-confirm__btn {',
    '  font-family: inherit; font-size: 0.8125rem; font-weight: 700;',
    '  padding: 8px 18px; border-radius: 6px; border: none; cursor: pointer;',
    '  text-transform: uppercase; letter-spacing: 0.04em;',
    '  transition: opacity 150ms ease; line-height: 1;',
    '}',
    '.bab-remove-confirm__btn:hover { opacity: 0.85; }',
    '.bab-remove-confirm__btn--remove { background: #a20067; color: #fff; }',
    '.bab-remove-confirm__btn--keep { background: #113e30; color: #fff; }',
    '.bab-remove-confirm__btn:disabled { opacity: 0.5; cursor: not-allowed; }',
  ].join('\n');
  document.head.appendChild(styleEl);
}

// ── Cart state ──────────────────────────────────────────────────────

function refreshCartState() {
  return fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then(function (res) { return res.json(); })
    .then(function (cart) {
      babLines = {};
      babKeys = new Set();

      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        if (item.properties && item.properties[BAB_PROP_KEY] === BAB_PROP_VALUE) {
          babLines[i + 1] = true;
          babKeys.add(item.key);
        }
      }

      updateDynamicCSS();
      injectBadges();
      return cart;
    })
    .catch(function () { return null; });
}

function updateDynamicCSS() {
  if (babKeys.size === 0) {
    babDynamicStyle.textContent = '';
    return;
  }

  const rules = [];
  babKeys.forEach(function (key) {
    rules.push('tr[data-key="' + key + '"] cart-quantity-selector-component { display: none !important; }');
  });
  babDynamicStyle.textContent = rules.join('\n');
}

function injectBadges() {
  babKeys.forEach(function (key) {
    const rows = document.querySelectorAll('tr[data-key="' + key + '"]');
    rows.forEach(function (row) {
      const qtyCell = row.querySelector('.cart-items__quantity-controls');
      if (qtyCell && !qtyCell.querySelector('.bab-bundle-badge')) {
        const badge = document.createElement('span');
        badge.className = 'bab-bundle-badge';
        badge.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>Soup Box';
        qtyCell.prepend(badge);
      }
    });
  });
}

// ── Prototype patching ──────────────────────────────────────────────

customElements.whenDefined('cart-items-component').then(function () {
  const Klass = customElements.get('cart-items-component');
  if (!Klass) return;

  const origRemove = Klass.prototype.onLineItemRemove;
  const origUpdate = Klass.prototype.updateQuantity;

  Klass.prototype.onLineItemRemove = function (line) {
    if (babLines[line]) {
      showRemoveConfirmation();
      return;
    }
    return origRemove.call(this, line);
  };

  Klass.prototype.updateQuantity = function (config) {
    if (config && babLines[config.line]) {
      showRemoveConfirmation();
      return;
    }
    return origUpdate.call(this, config);
  };
});

// ── Confirmation UI ─────────────────────────────────────────────────

function showRemoveConfirmation() {
  if (document.querySelector('.bab-remove-confirm')) return;

  const banner = document.createElement('div');
  banner.className = 'bab-remove-confirm';
  banner.setAttribute('role', 'alert');
  banner.innerHTML =
    '<p class="bab-remove-confirm__text">Removing this item will remove your entire Soup Box bundle from the cart.</p>' +
    '<div class="bab-remove-confirm__actions">' +
    '<button type="button" class="bab-remove-confirm__btn bab-remove-confirm__btn--remove">Remove Bundle</button>' +
    '<button type="button" class="bab-remove-confirm__btn bab-remove-confirm__btn--keep">Keep Items</button>' +
    '</div>';

  const cartComponent = document.querySelector('cart-items-component');
  if (cartComponent) {
    cartComponent.insertAdjacentElement('beforebegin', banner);
  }

  banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

  banner.querySelector('.bab-remove-confirm__btn--remove').addEventListener('click', function () {
    removeEntireBundle(banner);
  });

  banner.querySelector('.bab-remove-confirm__btn--keep').addEventListener('click', function () {
    window.location.reload();
  });
}

function removeEntireBundle(banner) {
  if (isRemoving) return;
  isRemoving = true;

  const buttons = banner.querySelectorAll('button');
  buttons.forEach(function (btn) { btn.disabled = true; });

  const removeBtn = banner.querySelector('.bab-remove-confirm__btn--remove');
  if (removeBtn) removeBtn.textContent = 'Removing...';

  fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then(function (res) { return res.json(); })
    .then(function (cart) {
      const updates = {};
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        if (item.properties && item.properties[BAB_PROP_KEY] === BAB_PROP_VALUE) {
          updates[item.key] = 0;
        }
      }

      if (Object.keys(updates).length === 0) {
        banner.remove();
        isRemoving = false;
        return;
      }

      return fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates }),
      }).then(function () {
        window.location.reload();
      });
    })
    .catch(function () {
      window.location.reload();
    });
}

// ── Init ────────────────────────────────────────────────────────────

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { refreshCartState(); });
} else {
  refreshCartState();
}

document.addEventListener('cart:update', function () {
  setTimeout(function () { refreshCartState(); }, 350);
});

let babBadgeTimer = null;
new MutationObserver(function () {
  if (babKeys.size === 0) return;
  if (babBadgeTimer) clearTimeout(babBadgeTimer);
  babBadgeTimer = setTimeout(injectBadges, 100);
}).observe(document.body, { childList: true, subtree: false });
